"use client"

import { useState, useMemo } from "react"
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { format, startOfWeek, parseISO } from 'date-fns'; // Add startOfWeek, parseISO

import { Button } from "../../components/ui/button"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../components/ui/tabs"
import { Plus } from "lucide-react"
import { Skeleton } from "../../components/ui/skeleton"
import { useToast } from "../../hooks/use-toast"

// Import API functions (add create/update)
import { getHabits, getHabitCompletions, toggleHabitCompletion, createHabit, updateHabit } from "../../services/api";
// Import Types
import { Habit, HabitCompletion } from "../../types";
// Import custom hooks (placeholders for now)
import { useCreateHabit, useUpdateHabit } from "../../hooks/useHabits";

// Import child components
import HabitItem from "../../components/habit_tracker/HabitItem"; // Import HabitItem directly
import AddEditHabitModal from "../../components/habit_tracker/AddEditHabitModal"

// Helper to get Monday of the week for a given YYYY-MM-DD string
const getWeekStartString = (dateStr: string): string => {
    const date = parseISO(dateStr);
    const monday = startOfWeek(date, { weekStartsOn: 1 }); // 1 for Monday
    return format(monday, 'yyyy-MM-dd');
};

// Define type for form data used in modal
type HabitFormData = {
    name: string;
    description: string | null;
    frequency: 'DAILY' | 'WEEKLY';
};

export default function HabitTrackerPage() {
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingHabit, setEditingHabit] = useState<Habit | null>(null);
    const queryClient = useQueryClient();
    const { toast } = useToast();

    // Get today's date and the start of the current week (Monday)
    const today = new Date();
    const todayStr = format(today, 'yyyy-MM-dd');
    const currentWeekStartStr = getWeekStartString(todayStr);

    // --- Queries --- 
    const { data: habits, isLoading: isLoadingHabits, error: errorHabits } = useQuery<Habit[], Error>({
        queryKey: ['habits'],
        queryFn: getHabits,
    });

    // Fetch completions for both today (for daily) and the start of the week (for weekly)
    // We might need to adjust API or query multiple times depending on API capabilities
    // Assuming getHabitCompletions can fetch for a date range or we query twice
    const { data: completions, isLoading: isLoadingCompletions, error: errorCompletions } = useQuery<HabitCompletion[], Error>({
        queryKey: ['habitCompletions', currentWeekStartStr, todayStr], // Use both dates in key
        queryFn: () => getHabitCompletions({ start_date: currentWeekStartStr, end_date: todayStr }), // Adjust API call if needed
        staleTime: 5 * 60 * 1000,
        gcTime: 10 * 60 * 1000,
    });

    // --- Mutations --- 
    const toggleMutation = useMutation({
        mutationFn: ({ habitId, date, currentState }: { habitId: number; date: string; currentState: boolean }) =>
            toggleHabitCompletion(habitId, date, currentState),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['habitCompletions', currentWeekStartStr, todayStr] });
            queryClient.invalidateQueries({ queryKey: ['userStats'] });
        },
        onError: (error) => {
            console.error("Error toggling habit:", error);
            toast({
                title: "Error",
                description: "Could not update habit completion. Please try again.",
                variant: "destructive",
            });
        },
    });

    // Call hooks unconditionally
    const { mutate: createHabitMutate, isPending: isCreating } = useCreateHabit();
    const { mutate: updateHabitMutate, isPending: isUpdating } = useUpdateHabit();

    // --- Event Handlers ---
    const handleToggleCompletion = (habitId: number, date: string) => {
        const habit = habits?.find(h => h.id === habitId);
        if (!habit) return;

        let isCurrentlyCompleted = false;
        if (habit.frequency === 'DAILY') {
            isCurrentlyCompleted = !!completions?.find(c => c.habit === habitId && c.completion_date === date);
        } else if (habit.frequency === 'WEEKLY') {
            const weekStart = getWeekStartString(date);
            isCurrentlyCompleted = !!completions?.find(c => c.habit === habitId && c.completion_date === weekStart);
        }
        toggleMutation.mutate({ habitId, date, currentState: isCurrentlyCompleted });
    };

    const handleOpenAddModal = () => {
        setEditingHabit(null);
        setIsModalOpen(true);
    };

    const handleOpenEditModal = (habit: Habit) => {
        setEditingHabit(habit);
        setIsModalOpen(true);
    };

    const handleSaveHabit = (habitData: HabitFormData, habitId?: number) => {
        const mutationOptions = {
            onSuccess: () => {
                toast({ title: habitId ? "Habit Updated" : "Habit Created", description: `"${habitData.name}" saved.` });
                setIsModalOpen(false);
                queryClient.invalidateQueries({ queryKey: ['habits'] });
            },
            onError: (error: Error) => {
                console.error("Error saving habit:", error);
                toast({ title: "Error", description: `Could not save habit. ${error.message}`, variant: "destructive" });
            },
        };

        if (habitId) {
            // Update - Pass only the fields collected from the form
            // The API expects Partial<Omit<...>>, so this is fine
            updateHabitMutate({ id: habitId, ...habitData }, mutationOptions);
        } else {
            // Create - Add default values for fields not in the form
            const createData = {
                ...habitData,
                start_date: format(new Date(), 'yyyy-MM-dd'), // Default to today
                is_active: true, // Default to active
                order: 0, // Default order
            };
            createHabitMutate(createData, mutationOptions);
        }
    };

    // --- Derived State & Rendering --- 
    // Use useMemo to prevent recalculating these on every render
    const dailyHabits = useMemo(() => habits?.filter(h => h.frequency === 'DAILY') ?? [], [habits]);
    const weeklyHabits = useMemo(() => habits?.filter(h => h.frequency === 'WEEKLY') ?? [], [habits]);

    // Create maps for quick completion lookup
    const dailyCompletionMap = useMemo(() => {
        const map = new Map<number, boolean>();
        completions?.forEach(c => {
            if (c.completion_date === todayStr) { // Only today's completions for daily map
                map.set(c.habit, true);
            }
        });
        return map;
    }, [completions, todayStr]);

    const weeklyCompletionMap = useMemo(() => {
        const map = new Map<number, boolean>();
        completions?.forEach(c => {
            // Check if completion date matches the start of the current week
            if (c.completion_date === currentWeekStartStr) {
                map.set(c.habit, true);
            }
        });
        return map;
    }, [completions, currentWeekStartStr]);

    if (errorHabits || errorCompletions) {
        return <div className="p-4 text-red-600">Error loading habit data. Please try refreshing.</div>;
    }

    // Loading state combines both queries
    const isLoading = isLoadingHabits || isLoadingCompletions;

    return (
        <div className="container mx-auto p-4 max-w-4xl">
            <div className="flex justify-between items-center mb-6">
                <h1 className="text-2xl font-bold">Habit Tracker</h1>
                <Button variant="default" onClick={handleOpenAddModal} disabled={isLoadingHabits}>
                    <Plus className="mr-2 h-4 w-4" /> Add New Habit
                </Button>
            </div>

            <Tabs defaultValue="daily" className="w-full">
                <TabsList className="mb-4 grid w-full grid-cols-2">
                    <TabsTrigger value="daily">Daily Habits</TabsTrigger>
                    <TabsTrigger value="weekly">Weekly Habits</TabsTrigger>
                </TabsList>

                <TabsContent value="daily">
                    {isLoading ? (
                        <div className="space-y-2 mt-4">
                            {[...Array(3)].map((_, i) => <Skeleton key={i} className="h-16 w-full" />)}
                        </div>
                    ) : dailyHabits.length > 0 ? (
                        <div className="space-y-2">
                            {dailyHabits.map((habit) => (
                                <HabitItem
                                    key={habit.id}
                                    habit={habit}
                                    isCompleted={dailyCompletionMap.get(habit.id) ?? false}
                                    onToggle={handleToggleCompletion}
                                    onEdit={handleOpenEditModal} // Pass edit handler
                                    currentDate={todayStr}
                                />
                            ))}
                        </div>
                    ) : (
                        <p className="text-center text-muted-foreground mt-4">No daily habits added yet.</p>
                    )}
                </TabsContent>

                <TabsContent value="weekly">
                    {isLoading ? (
                        <div className="space-y-2 mt-4">
                            {[...Array(2)].map((_, i) => <Skeleton key={i} className="h-16 w-full" />)}
                        </div>
                    ) : weeklyHabits.length > 0 ? (
                        <div className="space-y-2">
                            {weeklyHabits.map((habit) => (
                                <HabitItem
                                    key={habit.id}
                                    habit={habit}
                                    // Weekly completion is checked against the start of the week
                                    isCompleted={weeklyCompletionMap.get(habit.id) ?? false}
                                    onToggle={handleToggleCompletion}
                                    onEdit={handleOpenEditModal} // Pass edit handler
                                    currentDate={todayStr} // Pass today, backend logic determines key date
                                />
                            ))}
                        </div>
                    ) : (
                        <p className="text-center text-muted-foreground mt-4">No weekly habits added yet.</p>
                    )}
                </TabsContent>
            </Tabs>

            <AddEditHabitModal
                isOpen={isModalOpen}
                onClose={() => setIsModalOpen(false)}
                onSave={handleSaveHabit}
                initialData={editingHabit}
                // Pass loading state from create/update mutations
                isLoading={isCreating || isUpdating}
            />
        </div>
    )
} 